/**
 * Wishlist — storage layer
 * ============================================================================
 * Design: wishlist/WISHLIST-SPEC.md §4.
 *
 * Two backends behind one API. Callers never branch on login state:
 *
 *     theme.wishlist.get()              -> [handle, ...]  newest first
 *     theme.wishlist.has(handle)        -> bool
 *     theme.wishlist.toggle(handle, id) -> bool           optimistic
 *     theme.wishlist.add(handle, id)    -> bool
 *     theme.wishlist.remove(handle)     -> bool
 *     theme.wishlist.count()            -> int
 *     theme.wishlist.available()        -> bool           should the heart render?
 *
 * Guest    -> localStorage 'mustard-wishlist', mirroring 'theme-recent'.
 * Logged in -> customer metafield custom.wishlist, seeded server-side from
 *              Liquid at page load and mutated through the App Proxy.
 *
 * ⚠️ Zero edits to assets/theme.js — it is 8,625 lines of a paid theme that
 * receives Theme Store updates. All wishlist logic lives here.
 *
 * The synced backend stays dormant until layout/theme.liquid emits
 * #WishlistBootstrap with a customerId. Until the Shopify app exists, the guest
 * half runs alone and nothing is ever POSTed.
 */

window.theme = window.theme || {};

theme.wishlist = (function () {
  'use strict';

  var GUEST_KEY = 'mustard-wishlist';
  var QUEUE_KEY = 'mustard-wishlist-queue';
  var VERSION = 1;
  var CAP = 50;
  var PROXY = '/apps/wishlist';

  var RETRY_BASE = 1000;
  var RETRY_MAX = 60000;

  var state = {
    items: [],          // [{handle, id, added}] newest first
    customerId: null,   // null => guest
    hasStorage: false,
    retryDelay: RETRY_BASE,
    draining: false
  };

  /* ---------------------------------------------------------------------
     Storage primitives — every read is defensive. A truncated or hand-edited
     value must degrade to empty, never throw (spec §4).
  --------------------------------------------------------------------- */

  function storageOk() {
    // Reuse the theme's own probe where it exists. It returns false inside
    // iframes, which includes the theme editor preview.
    if (theme.config && typeof theme.config.hasLocalStorage === 'boolean') {
      return theme.config.hasLocalStorage;
    }
    // Fallback, mirroring theme.isStorageSupported('local') exactly. Deferred
    // scripts run with readyState 'interactive', so if wishlist.js is ever
    // ordered before theme.js the flag won't be set yet.
    if (window.self !== window.top) return false;
    try {
      window.localStorage.setItem('mustard-wishlist-test', '1');
      window.localStorage.removeItem('mustard-wishlist-test');
      return true;
    } catch (e) {
      return false;
    }
  }

  function readJSON(key, fallback) {
    if (!state.hasStorage) return fallback;
    try {
      var raw = window.localStorage.getItem(key);
      if (!raw) return fallback;
      var parsed = JSON.parse(raw);
      return parsed === null || parsed === undefined ? fallback : parsed;
    } catch (e) {
      return fallback;
    }
  }

  function writeJSON(key, value) {
    if (!state.hasStorage) return false;
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (e) {
      // Quota exceeded, or storage revoked mid-session. Not fatal.
      return false;
    }
  }

  function readGuest() {
    var data = readJSON(GUEST_KEY, null);
    if (!data || !Array.isArray(data.items)) return [];
    return data.items.filter(function (item) {
      return item && typeof item.handle === 'string' && item.handle.length;
    });
  }

  function writeGuest(items) {
    return writeJSON(GUEST_KEY, { v: VERSION, items: items });
  }

  function clearGuest() {
    if (!state.hasStorage) return;
    try {
      window.localStorage.removeItem(GUEST_KEY);
    } catch (e) {
      /* no-op */
    }
  }

  /* ---------------------------------------------------------------------
     Sync queue

     Optimistic UI: toggle() updates memory and fires its event immediately,
     then appends the op here. If the proxy is unreachable the heart still
     fills, the queue survives a reload, and it drains when the service
     returns. Never block the UI on the network — a heart that spins for 3s
     and then fails is worse than an unsynced heart.
  --------------------------------------------------------------------- */

  function readQueue() {
    var queue = readJSON(QUEUE_KEY, []);
    return Array.isArray(queue) ? queue : [];
  }

  function writeQueue(queue) {
    writeJSON(QUEUE_KEY, queue);
  }

  function enqueue(op) {
    if (!state.customerId) return;
    if (!state.hasStorage) {
      // A logged-in customer with no localStorage still works — the write just
      // goes straight out with no durable retry.
      send(op).catch(function () {});
      return;
    }
    var queue = readQueue();
    queue.push(op);
    writeQueue(queue);
    drain();
  }

  function send(op) {
    var path = PROXY + '/' + op.type;
    var body = op.type === 'merge' ? { handles: op.handles }
                                   : { handle: op.handle, source: op.source || null };
    return fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      if (response.ok) return response.json().catch(function () { return {}; });

      // 401 => no longer logged in. 404 => the app proxy isn't installed yet.
      // Neither is retryable, and both should stop us hammering the endpoint.
      if (response.status === 401 || response.status === 404) {
        var error = new Error('unauthenticated');
        error.fatal = true;
        throw error;
      }
      // 4xx other than 429 => a malformed or unknown handle. Dropping it keeps
      // one bad op from blocking the queue forever as a poison pill.
      if (response.status >= 400 && response.status < 500 && response.status !== 429) {
        var drop = new Error('rejected');
        drop.drop = true;
        throw drop;
      }
      throw new Error('retry'); // 429 and 5xx
    });
  }

  function drain() {
    if (state.draining || !state.customerId || !state.hasStorage) return;

    var queue = readQueue();
    if (!queue.length) {
      state.retryDelay = RETRY_BASE;
      return;
    }

    state.draining = true;
    send(queue[0]).then(function () {
      var rest = readQueue().slice(1);
      writeQueue(rest);
      state.draining = false;
      state.retryDelay = RETRY_BASE;
      if (rest.length) drain();
    }).catch(function (error) {
      state.draining = false;

      if (error && error.fatal) {
        writeQueue([]);            // logged out, or no proxy installed
        return;
      }
      if (error && error.drop) {
        writeQueue(readQueue().slice(1));
        drain();
        return;
      }
      // Network error, 429 or 5xx — back off and try again.
      window.setTimeout(drain, state.retryDelay);
      state.retryDelay = Math.min(state.retryDelay * 2, RETRY_MAX);
    });
  }

  /* ---------------------------------------------------------------------
     Merge on login (spec §4)

     Union, never overwrite: a customer who saved 3 items on their phone
     logged-out and has 10 on their account must end with 13, not 3.
  --------------------------------------------------------------------- */

  function mergeGuestIntoAccount() {
    var guest = readGuest();
    if (!guest.length) return;

    var handles = guest.map(function (item) { return item.handle; });

    // Merge into the in-memory list immediately so the UI is correct now,
    // without waiting for the round trip.
    var known = {};
    state.items.forEach(function (item) { known[item.handle] = true; });
    guest.forEach(function (item) {
      if (!known[item.handle]) state.items.push(item);
    });
    state.items = state.items.slice(0, CAP);

    fetch(PROXY + '/merge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ handles: handles })
    }).then(function (response) {
      if (!response.ok) return;
      clearGuest();      // only once the server has actually taken them
      emit(null, null);
    }).catch(function () {
      // Left in the guest store; the next login retries the merge.
    });
  }

  /* ---------------------------------------------------------------------
     Events
  --------------------------------------------------------------------- */

  function emit(handle, added) {
    document.dispatchEvent(new CustomEvent('wishlist:change', {
      detail: { handle: handle, added: added, count: state.items.length }
    }));
  }

  /* ---------------------------------------------------------------------
     Marketing analytics — the standard `add_to_wishlist` event (spec §7a).

     Fired on a genuine user ADD only (no "remove" — neither GA4 nor Meta has a
     standard remove-from-wishlist event). Emitted through every channel that
     might be present, each guarded, because the pixels are installed at the
     STORE level (Google/Meta apps or a custom pixel), not in this theme:
       - `dataLayer`  — GA4 via GTM / a dataLayer-based tag
       - `gtag`       — GA4 via a global gtag
       - `fbq`        — Meta pixel loaded on the page
       - `Shopify.analytics.publish` — the web-pixel sandbox, where the three
         globals above do NOT exist; a Custom Pixel can `analytics.subscribe`
         to `add_to_wishlist` and forward it to GA4/Meta.
     ⚠️ Analytics must never break a save — the whole thing is in a try/catch.
  --------------------------------------------------------------------- */
  function trackAdd(handle, id) {
    if (!handle) return;
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({ ecommerce: null });   // clear the previous ecommerce object (GA4/GTM pattern)
        window.dataLayer.push({
          event: 'add_to_wishlist',
          ecommerce: { items: [{ item_id: handle }] }
        });
      }
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'add_to_wishlist', { items: [{ item_id: handle }] });
      }
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'AddToWishlist', { content_ids: [handle], content_type: 'product' });
      }
      if (window.Shopify && window.Shopify.analytics &&
          typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('add_to_wishlist', { handle: handle, id: id || null });
      }
    } catch (e) { /* never let a tracking error block the save */ }
  }

  /* ---------------------------------------------------------------------
     Public API
  --------------------------------------------------------------------- */

  function get() {
    return state.items.map(function (item) { return item.handle; });
  }

  function has(handle) {
    return state.items.some(function (item) { return item.handle === handle; });
  }

  function count() {
    return state.items.length;
  }

  function persist() {
    if (!state.customerId) writeGuest(state.items);
  }

  function add(handle, id, source) {
    if (!handle || has(handle)) return true;

    state.items.unshift({ handle: handle, id: id || null, added: Date.now() });
    state.items = state.items.slice(0, CAP);
    persist();
    emit(handle, true);
    enqueue({ type: 'add', handle: handle, source: source || null });
    trackAdd(handle, id);   // GA4 / Meta add_to_wishlist (spec §7a)
    return true;
  }

  function remove(handle) {
    if (!handle || !has(handle)) return false;

    state.items = state.items.filter(function (item) { return item.handle !== handle; });
    persist();
    emit(handle, false);
    enqueue({ type: 'remove', handle: handle });
    return false;
  }

  /**
   * Empty the whole list — the wishlist page's "Clear all".
   *
   * ⚠️ One queued op per item, not a single "clear" op. The proxy's vocabulary
   * is add/remove/merge (spec §8) and inventing a fourth verb here would mean a
   * queue entry no server understands, silently stuck at the head and blocking
   * every write behind it. At a cap of 50 the cost is bounded.
   *
   * Fires ONE `wishlist:change` with a null handle rather than one per item:
   * listeners reconcile against `get()`, so a single event is enough and N
   * events would mean N reflows.
   */
  function clear() {
    if (!state.items.length) return 0;

    var cleared = state.items.slice();
    state.items = [];
    persist();
    emit(null, false);
    cleared.forEach(function (item) {
      enqueue({ type: 'remove', handle: item.handle });
    });
    return cleared.length;
  }

  function toggle(handle, id, source) {
    return has(handle) ? remove(handle) : add(handle, id, source);
  }

  /**
   * Should the heart render at all?
   *
   * A guest with no localStorage would get a heart that silently forgets, which
   * is worse than no heart. A logged-in customer with no localStorage is fine —
   * writes go straight to the proxy.
   */
  function available() {
    return !!state.customerId || state.hasStorage;
  }

  /* ---------------------------------------------------------------------
     DOM layer (spec §5)
  --------------------------------------------------------------------- */

  var BUTTON_SELECTOR = '[data-wishlist-toggle]';
  var syncQueued = false;

  function setButtonState(button, saved) {
    button.classList.toggle('is-active', saved);
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');

    // Update aria-label too, or a screen reader announces "Add to wishlist" on
    // an item that is already saved.
    var label = saved ? button.getAttribute('data-label-remove')
                      : button.getAttribute('data-label-add');
    if (label) {
      button.setAttribute('aria-label', label);
      var text = button.querySelector('[data-wishlist-label]');
      if (text) text.textContent = label;

      // The hover tooltip on the grid heart. It has to be rewritten here rather
      // than left to CSS: the tip is still on screen under the cursor at the
      // moment the heart is clicked, so a stale string reads as "Add to
      // wishlist" over a filled heart until the pointer leaves the card.
      //
      // ⚠️ This is an ATTRIBUTE, drawn by `.wishlist-btn--grid::after` through
      // `content: attr(data-wishlist-tip)` — not a child element. A real text
      // node renders as ordinary text whenever the stylesheet is missing, which
      // squashes the heart to 0px in the button's flex row. Only set it where
      // it already exists, so the PDP heart never grows one.
      if (button.hasAttribute('data-wishlist-tip')) {
        button.setAttribute('data-wishlist-tip', label);
      }
    }
  }

  function syncButtons(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var buttons = scope.querySelectorAll(BUTTON_SELECTOR);
    for (var i = 0; i < buttons.length; i++) {
      setButtonState(buttons[i], has(buttons[i].getAttribute('data-wishlist-handle')));
    }
  }

  function updateCounts() {
    var total = state.items.length;

    var bubbles = document.querySelectorAll('[data-wishlist-count]');
    for (var i = 0; i < bubbles.length; i++) {
      bubbles[i].textContent = total > 0 ? String(total) : '';
      bubbles[i].classList.toggle('wishlist-link__bubble--visible', total > 0);
    }

    // Plain inline counts (cart surfaces, §7) carry no bubble styling.
    var inline = document.querySelectorAll('[data-wishlist-count-inline]');
    for (var j = 0; j < inline.length; j++) {
      inline[j].textContent = total > 0 ? String(total) : '';
    }
  }

  function refresh(root) {
    syncButtons(root);
    updateCounts();
  }

  function queueRefresh() {
    if (syncQueued) return;
    syncQueued = true;
    var run = function () {
      syncQueued = false;
      refresh();
    };
    if (window.requestAnimationFrame) window.requestAnimationFrame(run);
    else window.setTimeout(run, 16);
  }

  function onDocumentClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;


    var button = target.closest(BUTTON_SELECTOR);
    if (!button) return;

    // Never let this reach card navigation or the theme's own delegated
    // handlers — hence capture phase plus both calls.
    event.preventDefault();
    event.stopPropagation();

    var handle = button.getAttribute('data-wishlist-handle');
    if (!handle) return;

    toggle(handle,
           parseInt(button.getAttribute('data-wishlist-id'), 10) || null,
           button.getAttribute('data-wishlist-source'));
  }

  /**
   * Collection filtering re-renders the grid server-side via `?section_id=`
   * (theme.js:1471), replacing the nodes wholesale. Without this, filtered
   * pages come back with every heart empty.
   *
   * Observing body rather than `.grid` on purpose: the grid element itself is
   * replaced, so an observer bound to it would go with it.
   */
  function observeRenders() {
    if (!window.MutationObserver) return;
    new window.MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
          queueRefresh();
          return;
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  function applyAvailability() {
    // A guest with no localStorage gets no heart at all — one that silently
    // forgets is worse than none. CSS hides them off this class.
    document.documentElement.classList.toggle('wishlist-unavailable', !available());
  }

  /* ---------------------------------------------------------------------
     Boot
  --------------------------------------------------------------------- */

  function readBootstrap() {
    var node = document.getElementById('WishlistBootstrap');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (e) {
      return null;
    }
  }

  function init() {
    state.hasStorage = storageOk();

    var boot = readBootstrap();
    if (boot && boot.customerId) {
      state.customerId = boot.customerId;
      // Seeded server-side from Liquid, so there is no fetch waterfall and no
      // flash of a wrong count (spec §6).
      state.items = (boot.items || []).filter(Boolean).map(function (handle) {
        return { handle: handle, id: null, added: 0 };
      });
      mergeGuestIntoAccount();
      drain();
    } else {
      state.items = readGuest();
    }

    // Keep open tabs consistent.
    window.addEventListener('storage', function (event) {
      if (!event) return;
      if (event.key === GUEST_KEY && !state.customerId) {
        state.items = readGuest();
        emit(null, null);
      }
      if (event.key === QUEUE_KEY) drain();
    });

    // A queue left over from a previous visit drains once we're back online.
    window.addEventListener('online', drain);

    applyAvailability();

    // One delegated listener, in capture phase so it runs before the theme's
    // own document-level handlers. Survives re-renders without rebinding.
    document.addEventListener('click', onDocumentClick, true);
    document.addEventListener('wishlist:change', function () { refresh(); });
    document.addEventListener('shopify:section:load', function (event) {
      refresh(event && event.target);
    });
    observeRenders();

    refresh();
    emit(null, null);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return {
    get: get,
    has: has,
    add: add,
    remove: remove,
    clear: clear,
    toggle: toggle,
    count: count,
    available: available,
    syncButtons: syncButtons,
    refresh: refresh,
    _state: state    // debugging only
  };
})();

/**
 * Wishlist page renderer — spec §6.
 * ============================================================================
 * Only the guest path needs this. A logged-in customer's cards are rendered by
 * Liquid straight off the `list.product_reference` metafield, so the section
 * arrives complete and this module does nothing but wire up removals.
 *
 * Guests keep only handles in localStorage, so each card is fetched as
 * `/products/<handle>?section_id=wishlist-item` — real `product-grid-item`
 * output, identical to a collection grid.
 */
theme.wishlistPage = (function () {
  'use strict';

  var CACHE_KEY = 'wishlist-cards';
  var CONCURRENCY = 6;

  var CONFIRM_TIMEOUT = 4000;

  var page, grid, emptyState, loading, toolbar, clearButton, intro;
  var confirmTimer = null;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ---------------------------------------------------------------------
     Card cache — sessionStorage, mirroring how Recently Viewed caches its
     product JSON. Rendered HTML rather than JSON, because the whole point is
     to reuse the theme's own card markup.
  --------------------------------------------------------------------- */

  function readCache() {
    if (!theme.config || !theme.config.hasSessionStorage) return {};
    try {
      return JSON.parse(window.sessionStorage.getItem(CACHE_KEY) || '{}') || {};
    } catch (e) {
      return {};
    }
  }

  function writeCache(cache) {
    if (!theme.config || !theme.config.hasSessionStorage) return;
    try {
      window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (e) {
      /* quota — the page still works, it just refetches next time */
    }
  }

  /* ---------------------------------------------------------------------
     Fetching
  --------------------------------------------------------------------- */

  /**
   * Pull the card out of a section-rendered response.
   *
   * ⚠️ The response is wrapped in `<div id="shopify-section-wishlist-item">`,
   * which is not a grid cell — appending it whole puts a full-width block
   * between the cards. Lift the `.grid__item` out.
   *
   * ⚠️ A handle that no longer exists does NOT 404 under `?section_id=` — it
   * comes back 200 with an empty `product`, which renders a blank card reading
   * "Sold Out" with `data-product-handle=""`. Verified against the store, so
   * the response status cannot be the only guard: check that the card is
   * actually the product we asked for.
   */
  function extractCard(html, handle) {
    var holder = document.createElement('div');
    holder.innerHTML = html;

    var card = holder.querySelector('.grid__item');
    if (!card) return null;
    if (card.getAttribute('data-product-handle') !== handle) return null;

    return card;
  }

  function fetchCard(handle) {
    return fetch(root() + 'products/' + encodeURIComponent(handle) + '?section_id=wishlist-item')
      .then(function (response) {
        // 404 => deleted or unpublished since it was saved. Drop it silently;
        // a wishlist full of "product not found" is worse than a shorter one.
        if (!response.ok) return null;
        return response.text();
      })
      .catch(function () {
        return null;
      });
  }

  /**
   * Resolve every handle to card HTML, at most CONCURRENCY requests in flight.
   * Returns handles in their stored order, dropping the ones that failed.
   */
  function loadCards(handles) {
    var cache = readCache();
    var results = new Array(handles.length);
    var next = 0;

    function worker() {
      if (next >= handles.length) return Promise.resolve();
      var index = next++;
      var handle = handles[index];

      if (cache[handle]) {
        results[index] = cache[handle];
        return worker();
      }

      return fetchCard(handle).then(function (html) {
        // Validate before caching, or a handle deleted upstream gets its blank
        // "Sold Out" card remembered for the rest of the session.
        if (html && extractCard(html, handle)) {
          results[index] = html;
          cache[handle] = html;
        }
        return worker();
      });
    }

    var workers = [];
    for (var i = 0; i < CONCURRENCY; i++) workers.push(worker());

    return Promise.all(workers).then(function () {
      writeCache(cache);
      return handles.map(function (handle, index) {
        return { handle: handle, html: results[index] };
      }).filter(function (entry) {
        return !!entry.html;
      });
    });
  }

  /* ---------------------------------------------------------------------
     Rendering
  --------------------------------------------------------------------- */

  function render(entries) {
    var fragment = document.createDocumentFragment();

    entries.forEach(function (entry) {
      var card = extractCard(entry.html, entry.handle);
      if (!card) return;
      card.setAttribute('data-wishlist-card', entry.handle);
      fragment.appendChild(card);
    });

    grid.appendChild(fragment);

    // Quick shop and collapsibles bind on init and know nothing about cards
    // that arrived later. This is the theme's own re-hook, used by collection
    // filtering and the recommendations section for exactly this reason.
    if (typeof theme.reinitProductGridItem === 'function') {
      theme.reinitProductGridItem(page);
    }
    theme.wishlist.refresh(grid);
  }

  function cardCount() {
    return grid.querySelectorAll('.grid__item').length;
  }

  /**
   * ⚠️ The toolbar and the empty state both start hidden and exactly one is
   * ever unhidden. Liquid cannot see a guest's localStorage, so deciding
   * server-side would flash the wrong one at every returning shopper.
   */
  function settle() {
    if (loading) loading.hidden = true;

    var total = cardCount();
    var isEmpty = total === 0;

    if (emptyState) emptyState.hidden = !isEmpty;
    grid.hidden = isEmpty;

    // The intro explains how to fill the list; the empty state already says
    // that, better. The <h1> above it stays either way — it is the page's only
    // landmark, and the empty state's own heading is an <h2>.
    if (intro) intro.hidden = isEmpty;

    if (toolbar) {
      toolbar.hidden = isEmpty;
      var text = toolbar.querySelector('[data-wishlist-count-text]');
      if (text) {
        var template = total === 1 ? toolbar.getAttribute('data-count-one')
                                   : toolbar.getAttribute('data-count-other');
        text.textContent = template ? template.replace('[n]', total) : '';
      }
      // Clearing the last item mid-confirmation would otherwise leave the
      // button asking a question about an empty list next time it appears.
      if (isEmpty) resetClear();
    }
  }

  /**
   * Reconcile the rendered grid against storage.
   *
   * ⚠️ Not "remove the one card named in the event". "Clear all" empties the
   * list in a single event with a null handle, and reconciling covers both that
   * and a single removal with one code path — so the two can never disagree.
   */
  function reconcile() {
    var saved = {};
    theme.wishlist.get().forEach(function (handle) { saved[handle] = true; });

    var cards = [].slice.call(grid.querySelectorAll('.grid__item'));
    cards.forEach(function (card) {
      // Guest cards are keyed on insertion; server-rendered ones carry only the
      // theme's own attribute, so fall back to that.
      var handle = card.getAttribute('data-wishlist-card') ||
                   card.getAttribute('data-product-handle');
      if (!handle || saved[handle]) return;
      if (card.parentNode) card.parentNode.removeChild(card);
    });

    settle();
  }

  /* ---------------------------------------------------------------------
     Clear all — two-step, no native dialog

     Clearing has no undo, so it must be confirmed; a `window.confirm` is
     jarring and cannot be styled. The button asks in place instead, and gives
     up on a timeout or on any click elsewhere — so an accidental first tap
     costs nothing and is never left armed.
  --------------------------------------------------------------------- */

  function resetClear() {
    if (confirmTimer) {
      window.clearTimeout(confirmTimer);
      confirmTimer = null;
    }
    if (!clearButton) return;
    clearButton.classList.remove('is-confirming');
    var label = clearButton.getAttribute('data-default-text');
    if (label) clearButton.textContent = label;
  }

  function armClear() {
    if (!clearButton) return;
    clearButton.classList.add('is-confirming');
    var label = clearButton.getAttribute('data-confirm-text');
    if (label) clearButton.textContent = label;
    confirmTimer = window.setTimeout(resetClear, CONFIRM_TIMEOUT);
  }

  function onClearClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (clearButton.classList.contains('is-confirming')) {
      resetClear();
      theme.wishlist.clear();   // fires wishlist:change -> reconcile()
      return;
    }
    armClear();
  }

  /* ---------------------------------------------------------------------
     Shared view — /pages/wishlist?w=<token> (spec §7a)

     A read-only snapshot someone shared. The owner chrome (head, toolbar,
     guest prompt, intro, empty state) is hidden and the shared banner shown;
     the cards are the SHARED handles, fetched the same per-handle way as a
     guest's own list. Hearts still work — they act on the VIEWER's own list,
     never the owner's — so the `wishlist:change` reconcile listener is
     deliberately NOT wired in this path: it would strip every card that isn't
     already in the viewer's own list.
  --------------------------------------------------------------------- */

  var PROXY_SHARE = '/apps/wishlist/share';

  function shareToken() {
    try {
      return new URLSearchParams(window.location.search).get('w');
    } catch (e) {
      var m = window.location.search.match(/[?&]w=([^&]+)/);
      return m ? decodeURIComponent(m[1]) : null;
    }
  }

  function wireSaveAll(button, handles) {
    if (!button) return;
    button.addEventListener('click', function () {
      handles.forEach(function (handle) {
        if (theme.wishlist && !theme.wishlist.has(handle)) {
          theme.wishlist.add(handle, null, 'shared');
        }
      });
      button.classList.add('is-done');
      button.disabled = true;
      var done = button.getAttribute('data-done-text');
      if (done) button.textContent = done;
      // Re-sync the hearts on the shared cards so they read as saved.
      if (theme.wishlist && theme.wishlist.refresh) theme.wishlist.refresh(grid);
    });
  }

  function initShared(token) {
    var head = page.querySelector('.wishlist-page__head');
    var guest = page.querySelector('[data-wishlist-guest]');
    var banner = page.querySelector('[data-wishlist-shared]');
    var errorBox = page.querySelector('[data-wishlist-shared-error]');
    var saveAll = banner ? banner.querySelector('[data-wishlist-save-all]') : null;

    if (head) head.hidden = true;
    if (guest) guest.hidden = true;
    if (toolbar) toolbar.hidden = true;
    if (intro) intro.hidden = true;
    if (emptyState) emptyState.hidden = true;
    if (banner) banner.hidden = false;
    if (loading) loading.hidden = false;

    fetch(PROXY_SHARE + '/' + encodeURIComponent(token), {
      headers: { 'Accept': 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('share ' + response.status);
      return response.json();
    }).then(function (data) {
      var handles = (data && data.handles) || [];
      if (!handles.length) throw new Error('empty');
      return loadCards(handles).then(function (entries) {
        render(entries);
        if (loading) loading.hidden = true;
        // Every shared product deleted upstream -> nothing to show, treat as gone.
        if (!cardCount()) throw new Error('all gone');
        wireSaveAll(saveAll, handles);
      });
    }).catch(function () {
      if (loading) loading.hidden = true;
      if (banner) banner.hidden = true;
      if (errorBox) errorBox.hidden = false;
    });
  }

  /* ---------------------------------------------------------------------
     Boot
  --------------------------------------------------------------------- */

  function init() {
    page = document.querySelector('[data-wishlist-page]');
    if (!page) return;

    grid = page.querySelector('[data-wishlist-grid]');
    emptyState = page.querySelector('[data-wishlist-empty]');
    loading = page.querySelector('[data-wishlist-loading]');
    toolbar = page.querySelector('[data-wishlist-toolbar]');
    intro = page.querySelector('[data-wishlist-intro]');
    clearButton = page.querySelector('[data-wishlist-clear]');
    if (!grid) return;

    // Shared snapshot? Render it read-only and skip all owner-list wiring.
    var token = shareToken();
    if (token) { initShared(token); return; }

    if (clearButton) {
      clearButton.addEventListener('click', onClearClick);
      // Any click that isn't the button itself disarms it. Capture phase so a
      // card's own handler cannot swallow it first.
      document.addEventListener('click', function (event) {
        if (!clearButton.classList.contains('is-confirming')) return;
        if (event.target && event.target.closest &&
            event.target.closest('[data-wishlist-clear]')) return;
        resetClear();
      }, true);
    }

    // The heart, the Remove button and Clear all all land here — reconciling
    // against storage covers all three without caring which fired.
    document.addEventListener('wishlist:change', function (event) {
      if (event.detail && event.detail.added === true) return;  // nothing to drop
      reconcile();
    });

    if (page.hasAttribute('data-wishlist-server-rendered')) {
      settle();
      return;
    }

    var handles = theme.wishlist.get();
    if (!handles.length) {
      settle();
      return;
    }

    if (loading) loading.hidden = false;

    loadCards(handles).then(function (entries) {
      render(entries);
      settle();
    }).catch(function () {
      settle();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return { init: init };
})();


/**
 * ============================================================================
 * theme.wishlistShare — the share sheet (spec §7a)
 * ============================================================================
 * The toolbar "Share" button mints a read-only snapshot token via the App
 * Proxy (`POST /apps/wishlist/share` with the current handles), then fills the
 * link + every channel href and opens the sheet. The token carries no customer
 * identity and the route never mutates the owner's list. Guests can share too
 * (the proxy route has no 401) — the handles come from localStorage either way.
 */
theme.wishlistShare = (function () {
  'use strict';

  var PROXY = '/apps/wishlist';
  var SHARE_TEXT = 'My Mustard Fashion wishlist';

  var openBtn, sheet, panel, urlInput, copyBtn;
  var waLink, emailLink, fbLink, xLink, nativeBtn, errorEl;
  var minted = null;      // { key: handles.join(','), url } — reused within a pageview
  var lastFocus = null;

  function q(sel) { return sheet ? sheet.querySelector(sel) : null; }

  function shareUrl(token) {
    // The share page IS this page, minus any token, plus ?w=<token>.
    return window.location.origin + window.location.pathname + '?w=' + encodeURIComponent(token);
  }

  function fillChannels(url) {
    var enc = encodeURIComponent(url);
    if (urlInput) urlInput.value = url;
    if (waLink) waLink.href = 'https://wa.me/?text=' + encodeURIComponent(SHARE_TEXT + ' ' + url);
    if (emailLink) emailLink.href = 'mailto:?subject=' + encodeURIComponent(SHARE_TEXT) + '&body=' + enc;
    if (fbLink) fbLink.href = 'https://www.facebook.com/sharer/sharer.php?u=' + enc;
    if (xLink) xLink.href = 'https://twitter.com/intent/tweet?url=' + enc + '&text=' + encodeURIComponent(SHARE_TEXT);
    if (nativeBtn) {
      var can = !!(navigator && navigator.share);
      nativeBtn.hidden = !can;
      nativeBtn._url = url;
    }
  }

  function setError(on) { if (errorEl) errorEl.hidden = !on; }

  function openSheet() {
    if (!sheet) return;
    lastFocus = document.activeElement;
    sheet.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    if (panel && panel.focus) panel.focus();
    document.addEventListener('keydown', onKey);
  }

  function closeSheet() {
    if (!sheet) return;
    sheet.hidden = true;
    document.documentElement.style.overflow = '';
    document.removeEventListener('keydown', onKey);
    resetCopy();
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function onKey(e) { if (e.key === 'Escape' || e.keyCode === 27) closeSheet(); }

  function resetCopy() {
    if (!copyBtn) return;
    copyBtn.classList.remove('is-copied');
    var label = copyBtn.getAttribute('data-copy');
    if (label) copyBtn.textContent = label;
  }

  function markCopied() {
    if (!copyBtn) return;
    copyBtn.classList.add('is-copied');
    var label = copyBtn.getAttribute('data-copied');
    if (label) copyBtn.textContent = label;
    window.setTimeout(resetCopy, 2000);
  }

  function fallbackCopy() {
    if (!urlInput) return;
    try {
      urlInput.removeAttribute('readonly');
      urlInput.select();
      document.execCommand('copy');
      urlInput.setAttribute('readonly', 'readonly');
      markCopied();
    } catch (e) { /* leave the link on screen to copy by hand */ }
  }

  function onCopy() {
    if (!urlInput || !urlInput.value) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(urlInput.value).then(markCopied, fallbackCopy);
    } else {
      fallbackCopy();
    }
  }

  function onNative() {
    var url = nativeBtn && nativeBtn._url;
    if (navigator.share && url) {
      navigator.share({ title: SHARE_TEXT, url: url }).catch(function () {});
    }
  }

  function open() {
    var handles = (theme.wishlist && theme.wishlist.get) ? theme.wishlist.get() : [];
    if (!handles.length) return;
    var key = handles.join(',');

    setError(false);
    resetCopy();

    // Reuse a token already minted for this exact list this pageview.
    if (minted && minted.key === key) {
      fillChannels(minted.url);
      openSheet();
      return;
    }

    if (openBtn) openBtn.disabled = true;

    fetch(PROXY + '/share', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ handles: handles })
    }).then(function (r) {
      if (!r.ok) throw new Error('share ' + r.status);
      return r.json();
    }).then(function (data) {
      if (!data || !data.token) throw new Error('no token');
      var url = shareUrl(data.token);
      minted = { key: key, url: url };
      fillChannels(url);
      openSheet();
    }).catch(function () {
      if (urlInput) urlInput.value = '';
      setError(true);
      openSheet();
    }).then(function () {
      if (openBtn) openBtn.disabled = false;
    });
  }

  function init() {
    openBtn = document.querySelector('[data-wishlist-share-open]');
    sheet = document.querySelector('[data-wishlist-share]');
    if (!openBtn || !sheet) return;

    panel = sheet.querySelector('.wl-share__panel');
    urlInput = q('[data-wishlist-share-url]');
    copyBtn = q('[data-wishlist-share-copy]');
    waLink = q('[data-wishlist-share-wa]');
    emailLink = q('[data-wishlist-share-email]');
    fbLink = q('[data-wishlist-share-fb]');
    xLink = q('[data-wishlist-share-x]');
    nativeBtn = q('[data-wishlist-share-native]');
    errorEl = q('[data-wishlist-share-error]');

    openBtn.addEventListener('click', open);
    if (copyBtn) copyBtn.addEventListener('click', onCopy);
    if (nativeBtn) nativeBtn.addEventListener('click', onNative);

    var closers = sheet.querySelectorAll('[data-wishlist-share-close]');
    for (var i = 0; i < closers.length; i++) {
      closers[i].addEventListener('click', closeSheet);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return { init: init, open: open, close: closeSheet };
})();
